import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { classifyDenialReason, DENIAL_CATEGORY_LABELS } from '@/lib/normalization/denialNormalization';
import { DenialCategoryBadge } from './DenialCategoryBadge';
import { AlertTriangle, Save } from 'lucide-react';

interface DenialReasonEntryProps {
  authorizationId: string;
  currentDenialReason?: string;
  currentDenialRawText?: string;
  onSaved?: () => void;
}

export function DenialReasonEntry({
  authorizationId,
  currentDenialReason,
  currentDenialRawText,
  onSaved,
}: DenialReasonEntryProps) {
  const [rawText, setRawText] = useState(currentDenialRawText || '');
  const [denialReason, setDenialReason] = useState(currentDenialReason || '');
  const [isSaving, setIsSaving] = useState(false);

  const classification = classifyDenialReason(rawText);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('authorization_requests')
        .update({
          denial_raw_text: rawText || null,
          denial_reason: denialReason || null,
          denial_category: classification.category,
          denial_category_confidence: classification.confidence,
        })
        .eq('id', authorizationId);

      if (error) throw error;

      toast.success('Denial information saved');
      onSaved?.();
    } catch (err) {
      toast.error('Failed to save denial information');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="border-red-500/30 bg-red-500/5">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-red-500">
          <AlertTriangle className="w-4 h-4" />
          Record Denial Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label className="text-xs">Payer's Raw Denial Text</Label>
          <Textarea
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            placeholder="Paste the exact denial text from the payer..."
            rows={3}
            className="text-sm"
          />
        </div>

        {rawText && (
          <div className="p-3 bg-background/50 rounded-lg space-y-2">
            <p className="text-xs text-muted-foreground">Auto-classified as:</p>
            <DenialCategoryBadge
              category={classification.category}
              confidence={classification.confidence}
              rawText={rawText}
            />
            {classification.matchedPattern && (
              <p className="text-xs text-muted-foreground">
                Matched pattern: "{classification.matchedPattern}"
              </p>
            )}
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-xs">Internal Denial Summary</Label>
          <Input
            value={denialReason}
            onChange={(e) => setDenialReason(e.target.value)}
            placeholder="Brief summary for internal use..."
          />
        </div>

        <Button onClick={handleSave} disabled={isSaving} size="sm" className="gap-1">
          <Save className="w-3 h-3" />
          Save Denial Info
        </Button>
      </CardContent>
    </Card>
  );
}
